import cx from 'classnames'
import Skeleton from 'react-loading-skeleton'
import { useTranslation } from 'react-i18next'
import { useState, useRef, useEffect, Fragment, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isNil, isNull, nth, forEach, isEmpty, sumBy, trim } from 'lodash'

import useOpenConferenceCall from 'features/ConferenceCall/hooks/useConferenceCall'
import useGlobalConfirmBox from 'features/GlobalConfirmBox/hooks/useGlobalConfirmBox'
import { MAX_IMAGE_DIMENSION, MAX_IMAGE_POST } from 'config'
import { getUrlsFromString } from 'lib/helpers'
import { isMobileDevice } from 'lib/device'
import {
  checkFileSize,
  checkTotalFileSize,
  getImageAttributeFromFile,
} from 'lib/image'
import { isBannedUrl } from 'lib/ban'
import { useBanWord, useUploadImage } from 'hooks'
import { useGetGroupQuery } from 'store/api/yay/group'
import { ROUTE_PATH } from 'lib/paths'
import { showErrorPopup } from 'store/actions/popup'
import { ERROR_POPUP } from 'features/GlobalErrorPopup/config'
import { alertCallInUse, isCallInUse } from 'lib/conferenceCall'
import { Screenshot, execCommand, getKeyCode, scanImage } from './helpers'
import { useGroupUserRole } from 'pages/Group/hooks/useGroupUserRole'
import { getMessageTags, useMention } from './useMention'
import { selectPostBoxModal } from 'store/actions'
import { useCurrentUser } from 'store/api/yay/user'
import { extractPost } from 'components/Post/PostText/helpers'
import {
  useCreateThreadPostMutation,
  useGetThreadQuery,
} from 'store/api/yay/thread'
import {
  clearPostBoxShareToTimeline,
  selectPostBoxShareToTimeline,
} from 'store/slices/postBox'
import {
  conferenceCallToPostContextSchema,
  groupToGroupContextSchema,
  logEvent,
  postCreatedToPostContextSchema,
  snowplowEvent,
} from 'lib/analytics'
import {
  useCreateConferenceCallMutation,
  useCreateNewSharePostMutation,
  useCreatePostMutation,
  useCreateRepostMutation,
  useEditPostMutation,
  useGetUrlMetaDataMutation,
  useValidateMutation,
} from 'store/api/yay/post'
import {
  Attachment,
  CallType,
  Post,
  PostCreate,
  PostCreated,
  UrlMetaData,
  User,
} from 'lib/_types'

import PostBoxDropzone from './PostBoxDropzone'
import ReplyTo from 'components/ReplyTo'
import PostBoxPhotos from './PostBoxPhotos'
import PostShareableGroup from 'components/Post/PostShareableGroup'
import MetaDataPreviewCard from '../MetaDataPreviewCard'
import PostShareablePost from 'components/Post/PostShareablePost'
import ReplyToTarget from 'components/ReplyToTarget'
import PostEditablePost from 'components/Post/PostEditablePost'
import PostThread from 'components/Post/PostThread'
import { MentionableList } from './MentionList'
import { useLangDetect } from 'hooks/useLangDetect'

const MAX_POST_TEXT_LIMIT = 500
const CHOICES_DEFAULT = ['', '']
let urlMetaDataTO: number = null

type LogParams = Parameters<typeof logEvent.post_create>[0]

const PostBox = ({
  onPostCreated,
  hideReplyTo,
  hideRepost,
  hideShareable,
  hideFooterCallMenu,
  hideFooterVideoCallMenu,
  withinModal,
  groupId,
  threadId,
  replyPost,
  replyPostMode,
  overlayAction,
}: {
  onPostCreated?: (createdPostData?: {
    createdPost: PostCreated
    parentPost: Post
  }) => void
  hideReplyTo?: boolean
  hideRepost?: boolean
  hideShareable?: boolean
  hideFooterCallMenu?: boolean
  hideFooterVideoCallMenu?: boolean
  withinModal?: boolean
  groupId?: string
  threadId?: string
  replyPost?: Post
  replyPostMode?: 'repost' | 'reply' | 'edit'
  overlayAction?: Function
}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const groupUserRole = useGroupUserRole({ groupId })
  const { isContainBanWord, showDetectedBanWordAlert } = useBanWord()
  const { detectLang } = useLangDetect()
  const { openConferenceCall } = useOpenConferenceCall()
  const { showConfirmBox } = useGlobalConfirmBox()

  const ___$contentEditable = useRef<HTMLDivElement>()
  const ___$submitButton = useRef<HTMLButtonElement>()
  const ___isComposing = useRef<boolean>() // `___isComposing` is when a user types in Japanese and selecting a proper kanji.

  const [__isLoading, __isLoadingSet] = useState(false)
  const [__text, __textSet] = useState('')
  const [__screenshots, __screenshotsSet] = useState<Screenshot[]>([])
  const [__choices, __choicesSet] = useState(CHOICES_DEFAULT)
  const [__isActivePostBox, __isActivePostBoxSet] = useState(false)
  const [__choicesActive, __choicesActiveSet] = useState(false)
  const [__postBoxColorIndex, __postBoxColorIndexSet] = useState(0)
  const [__postBoxFontSize, __postBoxFontSizeSet] = useState(0)
  const [__video, __videoSet] = useState(null)
  const [__urlMetaData, __urlMetaDataSet] = useState<UrlMetaData>(null)
  const [__isDraftEventLogged, __isDraftEventLoggedSet] = useState(false)

  const isContentEditableActive =
    (__text?.length ?? 0) === 0 && !__isActivePostBox

  const [__isActiveImagePanel, __isActiveImagePanelSet] = useState(false)
  const [__isActiveMenuFontSize, __isActiveMenuFontSizeSet] = useState(false)
  const [__isCreateVideoCallPostActive, __isCreateVideoCallPostActiveSet] =
    useState(false)
  const [__isCreateCallPostActive, __isCreateCallPostActiveSet] =
    useState(false)

  const postBoxModal = useSelector(selectPostBoxModal)
  const currentUserId = useCurrentUser()?.data?.id
  const shareToTimeline: {
    type?: 'group' | 'thread'
  } = useSelector(selectPostBoxShareToTimeline)

  const mention = useMention({
    isContentEditableActive,
    postRef: ___$contentEditable,
    updateText: __textSet,
    isMentionEnabled: isEmpty(shareToTimeline),
  })

  const groupRes = useGetGroupQuery({ groupId }, { skip: isNil(groupId) })
  const threadRes = useGetThreadQuery({ threadId }, { skip: isNil(threadId) })

  const [createPost] = useCreatePostMutation()
  const [createRepost] = useCreateRepostMutation()
  const [createConferenceCall] = useCreateConferenceCallMutation()
  const [editPost, editPostRes] = useEditPostMutation()
  const [createThreadPost] = useCreateThreadPostMutation()
  const [createNewSharePost] = useCreateNewSharePostMutation()
  const [validate] = useValidateMutation()

  const [getUrlMetaData, getUrlMetaDataRes] = useGetUrlMetaDataMutation()

  const { upload } = useUploadImage()

  const onTogglePostBox = (flag?: boolean) => {
    __isActivePostBoxSet((prevState) => {
      return isNull(flag) ? !prevState : flag
    })
  }

  const onChangeImageUpload =
    (index: number) =>
    async (e?: { target?: { files?: FileList | File[] } }) => {
      const files = Array.from(e?.target?.files ?? [])

      let resultScreenshots = [...(__screenshots ?? [])] // Copy it to change reference.

      // Check each image until passed image reach max number.
      for (
        let i = 0;
        i < files.length && resultScreenshots.length < MAX_IMAGE_POST;
        i++
      ) {
        const file = files[i]

        // Check single file size.
        if (!checkFileSize(file)) {
          dispatch(showErrorPopup(ERROR_POPUP.FILE_SIZE_OVER_MAX_LIMIT))
          continue
        }

        // Validate image dimension.
        const { naturalHeight, naturalWidth } = await getImageAttributeFromFile(
          file
        )

        if (
          file.type === 'image/gif' &&
          (naturalHeight > MAX_IMAGE_DIMENSION ||
            naturalWidth > MAX_IMAGE_DIMENSION)
        ) {
          dispatch(showErrorPopup(ERROR_POPUP.FILE_DIMENSION_OVER_MAX_LIMIT))
          continue
        }

        const newScreenshots = [...(resultScreenshots ?? [])] // Copy it to change reference.
        const newImage = {
          file,
          width: naturalWidth,
          height: naturalHeight,
        }

        // Assign new image to target position.
        if (newScreenshots[index + i]) {
          newScreenshots[index + i] = newImage
        } else {
          newScreenshots.push(newImage)
        }

        // Check new total file size.
        if (!checkTotalFileSize(newScreenshots.map((i) => i.file))) {
          dispatch(showErrorPopup(ERROR_POPUP.FILE_SIZE_OVER_MAX_LIMIT))
          continue
        }

        // Not allow gif if have more than 1 image.
        if (
          newScreenshots.length > 1 &&
          newScreenshots.some((i) => i.file.type === 'image/gif')
        ) {
          continue
        }

        // Not allow banned url.
        try {
          const url = await scanImage(file)

          if (isBannedUrl(url)) {
            dispatch(showErrorPopup(ERROR_POPUP.IMAGE_CONTAIN_BANNED_URL))
            continue
          }
        } catch {
          // Presumably no QA code detected.
        }

        resultScreenshots = newScreenshots
      }

      __screenshotsSet(resultScreenshots)
    }

  const onToggleList = () => {
    __choicesSet(CHOICES_DEFAULT)
    __choicesActiveSet((prevState) => !prevState)
  }

  const onToggleMenuFontSize = () => {
    __isActiveMenuFontSizeSet((prevState) => {
      return !prevState
    })
  }

  const onInput = () => {
    __textSet(___$contentEditable.current?.innerText)
  }

  const resetStates = () => {
    __postBoxColorIndexSet(0)
    __postBoxFontSizeSet(0)
    __isActivePostBoxSet(false)
    __isCreateVideoCallPostActiveSet(false)
    __isActiveImagePanelSet(false)
    __isCreateCallPostActiveSet(false)
    __choicesActiveSet(false)
    __screenshotsSet([])
    __videoSet(null)
    __choicesSet(CHOICES_DEFAULT)
    __textSet('')
    __isLoadingSet(false)
    __urlMetaDataSet(null)
    __isDraftEventLoggedSet(false)
    getUrlMetaDataRes?.reset?.()
  }

  const isDirty = Boolean(
    __text ||
      __postBoxColorIndex > 0 ||
      __isActiveImagePanel ||
      __screenshots.length ||
      __choicesActive ||
      __isActiveMenuFontSize ||
      __postBoxFontSize > 0 ||
      __isCreateCallPostActive ||
      __isCreateVideoCallPostActive
  )

  const logDraftEvent = () => {
    if (
      replyPostMode === 'edit' ||
      !isEmpty(shareToTimeline) ||
      __isDraftEventLogged
    )
      return

    snowplowEvent.draft_post({
      group: groupToGroupContextSchema({
        group: groupRes?.data,
        currentUserId,
      }),
    })

    __isDraftEventLoggedSet(true)
  }

  useEffect(() => {
    if (__isActivePostBox) return

    if (isDirty) logDraftEvent()
    else __isDraftEventLoggedSet(false)
  }, [
    __postBoxColorIndex,
    __isActiveImagePanel,
    __choicesActive,
    __isActiveMenuFontSize,
    __postBoxFontSize,
    __isCreateCallPostActive,
    __isCreateVideoCallPostActive,
  ])

  useEffect(() => {
    if (__isActivePostBox) {
      if (___$contentEditable.current) {
        ___$contentEditable.current?.focus?.()

        if (!isDirty) logDraftEvent()

        // Move text cursor to the end of the input.
        window.setTimeout(() => {
          let range = document.createRange()
          range.selectNodeContents(___$contentEditable.current)
          range.collapse(false)

          let selection = window.getSelection()
          selection.removeAllRanges()
          selection.addRange(range)
        }, 10)
      }
    } else {
      if (!isDirty) __isDraftEventLoggedSet(false)
    }
  }, [__isActivePostBox])

  useEffect(() => {
    if (!__isActiveImagePanel) {
      __screenshotsSet([])
    }
  }, [__isActiveImagePanel])

  const _isActive = postBoxModal?.isActive
  useEffect(() => {
    if (withinModal) {
      // Auto focus text input when postbox modal activates.
      window.setTimeout(() => {
        onTogglePostBox(_isActive)

        window.setTimeout(() => {
          if (replyPostMode == 'edit') {
            const extractedPost = extractPost(replyPost)
            let _text = ''

            extractedPost?.forEach?.((x) => {
              switch (x.type) {
                case 'user': {
                  _text = `${_text}${
                    mention.generateMentionSpanTag(
                      x?.target
                        ? {
                            user: {
                              id: x?.target,
                              nickname: x?.text?.slice?.(1), // Getting rid of `@`.
                            } as User,
                          }
                        : {
                            type: 'all',
                          }
                    )?.outerHTML
                  }`

                  break
                }
                case 'break': {
                  _text = `${_text}<br />`
                  break
                }
                case 'space': {
                  _text = `${_text} `
                  break
                }
                default: {
                  _text = `${_text}${x?.text}`
                  break
                }
              }
            })

            // If a post's text is exactly just a mentioning user's name,
            // when you edit the post, your cursor will end up in `span.Mention`
            // and if you type anything, the `span.Mention` will be deleted.
            //
            // To fix this, just add another whitespace at the end.
            // The cursor will be properly placed.
            if (
              trim(replyPost?.text)?.length ==
              sumBy(replyPost?.message_tags, (mt) => mt?.length)
            ) {
              _text = `${_text}&nbsp;`
            }

            ___$contentEditable.current.innerHTML = _text

            onInput()

            __postBoxColorIndexSet(replyPost?.color)
            __postBoxFontSizeSet(replyPost?.font_size)
          }
        }, 10)
      }, 10)

      if (!_isActive) {
        dispatch(clearPostBoxShareToTimeline())
        resetStates()
      }
    }
  }, [_isActive])

  useEffect(() => {
    window.clearTimeout(urlMetaDataTO)
    urlMetaDataTO = window.setTimeout(() => {
      if (
        !__isActiveImagePanel &&
        !__isCreateVideoCallPostActive &&
        !__isCreateCallPostActive &&
        !__choicesActive
      ) {
        const url = getUrlsFromString(__text)?.[0] ?? null

        if (
          !isNil(url) &&
          !getUrlMetaDataRes?.isSuccess &&
          !getUrlMetaDataRes?.isLoading
        ) {
          getUrlMetaData({ url }).then((res) => {
            __urlMetaDataSet(res?.['data'])
          })
        }
      } else if (
        (__isActiveImagePanel ||
          __isCreateVideoCallPostActive ||
          __isCreateCallPostActive ||
          __choicesActive) &&
        getUrlMetaDataRes?.isSuccess
      ) {
        getUrlMetaDataRes?.reset?.()
        __urlMetaDataSet(null)
      }
    }, 500)
  }, [__text])

  const onShareToTimeline = (logParams: LogParams) => {
    let _shareData: {
      shareableType: 'group' | 'thread'
      shareableId: number
      text: string
      groupId?: number
      language?: string
    } = {
      shareableType: shareToTimeline?.type,
      shareableId: null,
      text: __text,
      language: detectLang(__text),
    }

    if (shareToTimeline?.type == 'group') {
      _shareData = {
        ..._shareData,
        shareableId: groupRes?.data?.id,
      }
    } else {
      _shareData = {
        ..._shareData,
        shareableId: threadRes?.data?.id,
        groupId: groupRes?.data?.id,
      }
    }

    createNewSharePost(_shareData).then((result) => {
      if (result?.['data']?.result == 'success') {
        const post = result['data']?.post as PostCreated

        if (post?.id) {
          logEvent.post_create({
            ...logParams,
            type: post.post_type,
          })

          snowplowEvent.create_post({
            post: postCreatedToPostContextSchema({
              post,
              userId: currentUserId,
              location: 'timeline',
            }),
          })
        }

        if (shareToTimeline?.type == 'thread') {
          window.location.href = ROUTE_PATH.GROUP(groupRes?.data?.id)
        } else {
          window.location.href = ROUTE_PATH.HOME
        }
      }
    })
  }

  const onCreateCall = async (data: PostCreate, logParams: LogParams) => {
    // Prevent to create new call, if user is in conference call
    if (isCallInUse()) {
      alertCallInUse()
      resetStates()
      return
    }

    if (__isCreateVideoCallPostActive) {
      data.call_type = CallType.Video
    }

    const res = await createConferenceCall(data)

    if (res?.['data']?.result == 'success') {
      logEvent.post_create({
        ...logParams,
        type: 'call',
      })

      snowplowEvent.create_post({
        post: conferenceCallToPostContextSchema({
          conference: res?.['data']?.conference_call,
          userId: currentUserId,
          location: groupRes?.data ? 'group' : 'timeline',
        }),
        group: groupToGroupContextSchema({
          group: groupRes?.data,
          currentUserId,
        }),
      })

      openConferenceCall({
        conferenceCallId: res?.['data']?.conference_call?.id,
        allowOnMobile: true,
        inviteId: null,
        targetId: null,
      })

      resetStates()
      onPostCreated?.()
    }
  }

  const hasGifImage = useMemo(
    () => __screenshots.some(({ file }) => file?.type === 'image/gif'),
    [__screenshots]
  )

  return (
    <>
      <form
        onSubmit={async (e) => {
          e.preventDefault()

          ___$contentEditable.current?.blur?.()
          __isLoadingSet(true)

          const logParams: LogParams = {
            type: 'text',
            where_to: groupId ? 'group' : 'timeline',
            target_user_id: null,
          }

          // Validate banned word in text.
          if (isContainBanWord(__text)) {
            showDetectedBanWordAlert()
            __isLoadingSet(false)
            return
          }

          // Share group or thread.
          if (!isEmpty(shareToTimeline) && groupRes?.data?.id) {
            onShareToTimeline(logParams)
            return
          }

          const _groupId = groupId // `groupId` prop will change in a modal, we need to cache this here.
          const _threadId = threadId // Same goes for `threadId`.

          // Validate group permission for posting url.
          if (
            !isNil(_groupId) &&
            !groupRes?.data?.allow_members_to_post_url &&
            !['moderator', 'owner'].includes(groupUserRole)
          ) {
            const validateRes = await validate({
              text: __text,
              groupId: _groupId,
              threadId: _threadId,
            })

            if (validateRes?.['data']?.result == false) {
              showConfirmBox({
                title: t('confirm_box_group_allow_members_to_post_url.title'),
                content: t(
                  'confirm_box_group_allow_members_to_post_url.content'
                ),
                confirmBoxProps: {
                  cancelContent: <></>,
                },
              })

              __isLoadingSet(false)
              return
            }
          }

          // Basic post data.
          let postMethod: (params: PostCreate) => Promise<any> = createPost
          const data: PostCreate = {
            post_type: 'text',
            text: __text ?? '',
            language: detectLang(__text),
            color: __postBoxColorIndex?.toString?.(),
            font_size: __postBoxFontSize?.toString?.(),
            message_tags: JSON.stringify(
              getMessageTags(___$contentEditable.current)
            ),
          }

          if (_groupId && (hideShareable || isEmpty(shareToTimeline?.type))) {
            data.group_id = _groupId
          }

          // Create call post
          if (__isCreateCallPostActive || __isCreateVideoCallPostActive) {
            onCreateCall(data, logParams)
            return
          }

          // Normal create post flow.

          // Check this text post with shareable url first.
          // This type is given the lowest priority because it was autofilled from system.
          // `post_type` will be override by other type from what user do by below code.
          // TODO: maybe disable other post options if postbox contains `urlMetaData`.
          if (!isNil(__urlMetaData)) {
            data.shared_url = JSON.stringify(__urlMetaData)
            data.post_type = 'shareable_url'
            logParams.type = 'shareable_url'
          }

          // Image post.
          if (__screenshots?.length > 0) {
            logParams.type = 'image'
            data.post_type = 'image'
            data.attachment_sizes = {}

            const _files: Array<File> = []

            forEach(__screenshots, ({ file, width, height }, i) => {
              const key = i === 0 ? 'attachment' : `attachment_${Number(i) + 1}`

              _files.push(file)
              data.attachment_sizes[key] = [width, height]
            })

            let resUpload: Array<Attachment>

            try {
              resUpload = await upload(_files, 'post')
            } catch {
              __isLoadingSet(false)
              return
            }

            resUpload
              ?.filter((x) => !x.isThumb)
              .forEach?.((x, i) => {
                const key = `attachment${i === 0 ? '' : `_${i + 1}`}_filename`
                data[key] = x?.fileName
              })
          }

          // Survey post.
          if (__choicesActive) {
            if ((__choices ?? []).some((x) => isContainBanWord(x))) {
              showDetectedBanWordAlert()
              __isLoadingSet(false)
              return
            }

            data['choices'] = __choices ?? []
            data.post_type = 'survey'
            logParams.type = 'survey'
          }

          // Post with member mention.
          const mentions = (e?.target as HTMLFormElement)?.elements?.[
            'mention_ids'
          ]?.value

          if (mentions) {
            data['mention_ids'] = mentions.split(',')
          }

          // Post relate to other post.
          if (replyPost?.id) {
            const replyId = replyPost?.id?.toString?.()

            switch (replyPostMode) {
              case 'reply':
                data.in_reply_to = replyId
                break

              case 'repost':
                postMethod = createRepost
                data.post_id = replyId
                logParams.type = 'repost'
                break

              case 'edit':
                postMethod = editPost
                data.post_id = replyId
                break
            }
          }

          if (threadId && replyPostMode != 'edit') {
            postMethod = (data) => {
              return createThreadPost({
                threadId: threadId ?? replyPost?.thread_id,
                data,
              })
            }
          }

          postMethod(data).then((res) => {
            const createdPost = res?.['data'] as PostCreated

            if (createdPost?.id) {
              if (replyPostMode != 'edit') {
                logEvent.post_create(logParams)

                snowplowEvent.create_post({
                  post: postCreatedToPostContextSchema({
                    post: createdPost,
                    userId: currentUserId,
                    location: groupRes?.data ? 'group' : 'timeline',
                  }),
                  group: groupToGroupContextSchema({
                    group: groupRes?.data,
                    currentUserId,
                  }),
                })
              }

              if (replyPostMode === 'repost' || replyPostMode === 'edit') {
                // The new post might not be there immediatelty, let's wait a bit here.
                window.setTimeout(() => {
                  if (replyPostMode === 'edit') {
                    window.location.reload()
                  } else if (_threadId) {
                    window.location.href = ROUTE_PATH.GROUP_THREAD(
                      _groupId,
                      _threadId
                    )
                  } else if (_groupId) {
                    window.location.href = ROUTE_PATH.GROUP(_groupId)
                  } else {
                    window.location.href = ROUTE_PATH.HOME
                  }
                }, 100)

                return
              }

              resetStates()
              onPostCreated?.({
                createdPost,
                parentPost: replyPost,
              })
            }
          })
        }}
        className={cx('PostBox', {
          'PostBox--active': __isActivePostBox,
        })}
      >
        <a
          className="PostBox__overlay"
          onClick={() => {
            onTogglePostBox()
          }}
        />

        <div
          className={cx('PostBox__wrapper', {
            'PostBox__wrapper--reply': replyPostMode === 'reply',
            'PostBox__wrapper--disabled': __isLoading,
          })}
        >
          <PostBoxDropzone
            onDrop={(files: Array<File>) => {
              if (!__isActiveImagePanel) {
                __isActivePostBoxSet(true)
                __isCreateCallPostActiveSet(false)
                __isCreateVideoCallPostActiveSet(false)
                __isActiveImagePanelSet(true)
                __choicesActiveSet(false)

                if (__postBoxColorIndex >= 1000) {
                  // If background text is active, change it to default.
                  __postBoxColorIndexSet(0)
                }
              }

              onChangeImageUpload(__screenshots?.length)({
                target: { files },
              })
            }}
            onDropRejected={() => {
              dispatch(showErrorPopup(ERROR_POPUP.INVALID_IMAGE_FORMAT_UPLOAD))
            }}
            accept={{
              'image/png': [],
              'image/jpeg': ['.jpg', '.jpeg'],
              ...(__screenshots.length === 0 && { 'image/gif': [] }),
            }}
            disabled={
              __isCreateVideoCallPostActive ||
              __isCreateCallPostActive ||
              __choicesActive ||
              __screenshots?.length >= MAX_IMAGE_POST ||
              hasGifImage ||
              replyPostMode == 'edit'
            }
          >
            <div
              className={cx('PostBox__body', {
                [`PostBox__body--color-${__postBoxColorIndex}`]:
                  replyPostMode != 'reply',
                [`PostBox__body--fz-${__postBoxFontSize}`]:
                  replyPostMode != 'reply',
                'PostBox__body--ov-s': withinModal,
              })}
            >
              <ReplyToTarget
                post={replyPost}
                replyPostMode={replyPostMode}
                group={groupRes}
              >
                <Fragment>
                  {!hideReplyTo &&
                    !isNil(replyPost?.id) &&
                    replyPostMode === 'reply' && (
                      <div className="PostBox__body__reply-to">
                        <ReplyTo
                          post={replyPost}
                          repliedPost={replyPost} // Note `repliedPost` here is different. We need to refer to replying post, not the post in `in_reply_to_post`.
                          chooseRepliers
                        />
                      </div>
                    )}

                  {(() => {
                    const placeholder =
                      shareToTimeline?.type == 'thread' && !hideShareable
                        ? t('postbox.textarea.placeholder_thread')
                        : replyPostMode == 'repost'
                        ? t('postbox.textarea.placeholder_repost')
                        : __choicesActive
                        ? t('postbox.textarea.placeholder_survey')
                        : t('postbox.textarea.placeholder')

                    return (
                      <div
                        className={cx('PostBox__body__input-wrapper', {
                          [`PostBox__body__input-wrapper--reply-mode`]:
                            replyPostMode == 'reply',
                          [`PostBox__body__input-wrapper--reply-mode--color-${__postBoxColorIndex}`]:
                            replyPostMode == 'reply',
                          [`PostBox__body__input-wrapper--reply-mode--fz-${__postBoxFontSize}`]:
                            replyPostMode == 'reply',
                        })}
                      >
                        {isContentEditableActive ? (
                          <textarea
                            placeholder={placeholder}
                            name="text"
                            rows={1}
                            onClick={() => {
                              onTogglePostBox(true)
                            }}
                          />
                        ) : (
                          <div
                            contentEditable
                            ref={___$contentEditable}
                            placeholder={placeholder}
                            onClick={() => {
                              onTogglePostBox(true)
                            }}
                            onCompositionStart={() => {
                              ___isComposing.current = true
                            }}
                            onCompositionEnd={() => {
                              ___isComposing.current = false
                            }}
                            onKeyDown={(e) => {
                              // Prevent Japanese IME bug on safari, when press enter to select text its submit form.
                              if (getKeyCode(e) === 229 && e.key === 'Enter') {
                                e.preventDefault()
                                return
                              }

                              if (
                                (e.ctrlKey || e.metaKey) &&
                                (e.key === 'i' || e.key === 'b')
                              ) {
                                e.preventDefault()
                              }

                              // Press Enter to send.
                              // Press Enter + Shift to newline.
                              if (
                                e.key.toLowerCase() === 'enter' &&
                                !e.shiftKey &&
                                !___isComposing.current &&
                                !isMobileDevice
                              ) {
                                e.preventDefault()

                                if (___$submitButton.current) {
                                  ___$submitButton.current?.click?.()
                                }
                              }
                            }}
                            onPaste={(e) => {
                              e.preventDefault()

                              const text =
                                e?.clipboardData?.getData?.('text/plain')

                              execCommand(text)
                            }}
                            onInput={onInput}
                          />
                        )}
                      </div>
                    )
                  })()}

                  {__isActiveImagePanel && (
                    <PostBoxPhotos
                      screenshots={__screenshots ?? []}
                      hasGifImage={hasGifImage}
                      onChangeImageUpload={onChangeImageUpload}
                      onDeleteImageUpload={(index: number) => () => {
                        __screenshotsSet((prevState) => {
                          return (prevState ?? [])?.filter?.(
                            (_, i) => i !== index
                          )
                        })
                      }}
                    />
                  )}

                  {/* There used to be a YouTube component right here. If there is any need to resurrect it, please check out https://github.com/nanameue/yay_web/pull/637. */}

                  {__choicesActive && (
                    <div className="PostBox__body__survey">
                      <ul className="PostBox__body__survey__ul">
                        {(__choices ?? []).map((_, i) => (
                          <li key={i} className="PostBox__body__survey__ul__li">
                            <input
                              type="text"
                              className="PostBox__body__survey__ul__input"
                              placeholder={t('postbox.survey.placeholder', {
                                index: i + 1,
                              })}
                              value={nth(__choices, i)}
                              onChange={(e) => {
                                const $el = e.target

                                __choicesSet((prevState) => {
                                  return (prevState ?? [])?.map?.((y, j) => {
                                    if (i == j) {
                                      return $el.value
                                    }

                                    return y
                                  })
                                })
                              }}
                            />

                            <a
                              onClick={() => {
                                if (i == (__choices?.length ?? 0) - 1) {
                                  // Last button
                                  if (__choices?.length < 6) {
                                    __choicesSet((prevState) => {
                                      return [...prevState, '']
                                    })
                                  }
                                } else {
                                  if (i == 0 && (__choices?.length ?? 0) == 2) {
                                    onToggleList()
                                  } else {
                                    __choicesSet((prevState) => {
                                      return (prevState ?? []).filter(
                                        (_, j) => i != j
                                      )
                                    })
                                  }
                                }
                              }}
                              className={cx('PostBox__body__survey__ul__a', {
                                'PostBox__body__survey__ul__a--add':
                                  i == (__choices?.length ?? 0) - 1,
                                'PostBox__body__survey__ul__a--disabled':
                                  i == (__choices?.length ?? 0) - 1
                                    ? (__choices?.length ?? 0) == 6 // Last button
                                    : false,
                              })}
                            />
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {replyPostMode != 'edit' && !isNil(__urlMetaData) && (
                    <MetaDataPreviewCard
                      url={__urlMetaData?.url}
                      title={__urlMetaData?.title}
                      description={__urlMetaData?.description}
                      imageUrl={__urlMetaData?.image_url}
                      isLoading={getUrlMetaDataRes?.isLoading}
                      onClose={() => {
                        __urlMetaDataSet(null)
                      }}
                    />
                  )}

                  {!hideRepost &&
                    replyPost?.id &&
                    replyPostMode == 'repost' && (
                      <PostShareablePost
                        post={replyPost}
                        group={groupRes}
                        disabled
                      />
                    )}

                  {replyPostMode == 'edit' && (
                    <PostEditablePost post={replyPost} group={groupRes} />
                  )}

                  {!hideShareable && (
                    <Fragment>
                      {shareToTimeline?.type == 'group' && groupRes?.data?.id && (
                        <PostShareableGroup
                          show={false}
                          disabled
                          post={{
                            shareable: {
                              group: groupRes?.data,
                            },
                          }}
                        />
                      )}

                      {shareToTimeline?.type == 'thread' &&
                        threadRes?.data?.id && (
                          <PostThread thread={threadRes?.data} disabled />
                        )}
                    </Fragment>
                  )}
                </Fragment>
              </ReplyToTarget>
            </div>

            <div className="PostBox__styles__wrapper">
              {(() => {
                let textStyleList = [
                  0, 1, 2, 3, 4, 5, 6, 7, 1001, 1002, 1003, 1004, 1005, 1006,
                  1007,
                ]

                return (
                  <ul className="PostBox__styles">
                    {textStyleList.map((textStyle, i) => (
                      <li key={i}>
                        <a
                          className={cx('PostBox__styles__a', {
                            [`PostBox__styles__a--color-${textStyle}`]: true,
                            'PostBox__styles__a--active':
                              __postBoxColorIndex === textStyle,
                            'PostBox__styles__a--disabled':
                              (textStyle > 1000 &&
                                (__video ||
                                  __isActiveImagePanel ||
                                  __choicesActive ||
                                  __isCreateVideoCallPostActive ||
                                  __isCreateCallPostActive ||
                                  (postBoxModal?.mode === 'edit' &&
                                    postBoxModal?.post?.post_type !==
                                      'text'))) ||
                              !isEmpty(shareToTimeline),
                          })}
                          onClick={() => {
                            __postBoxColorIndexSet(textStyle)
                          }}
                        >
                          Aa
                        </a>
                      </li>
                    ))}
                  </ul>
                )
              })()}
            </div>

            <div className="PostBox__footer">
              {(() => {
                const _shouldDisableMenuBase =
                  replyPostMode === 'repost' ||
                  __video ||
                  __postBoxColorIndex > 1000 ||
                  !isEmpty(shareToTimeline)

                return (
                  <ul className="PostBox__footer__ul">
                    {/* Image upload. */}
                    <li className="PostBox__footer__ul__li">
                      <a
                        className={cx('PostBox__footer__ul__a', {
                          'PostBox__footer__ul__a--file': true,
                          'PostBox__footer__ul__a--file--active':
                            __isActiveImagePanel,
                          'PostBox__footer__ul__a--disabled':
                            _shouldDisableMenuBase ||
                            __choicesActive ||
                            __isCreateCallPostActive ||
                            __isCreateVideoCallPostActive ||
                            replyPostMode == 'edit',
                        })}
                        onClick={() => {
                          if (
                            !isNil(groupId) &&
                            !groupRes?.data
                              ?.allow_members_to_post_image_and_video &&
                            !['moderator', 'owner'].includes(groupUserRole)
                          ) {
                            showConfirmBox({
                              title: t(
                                'confirm_box_group_allow_members_to_post_image_and_video.title'
                              ),
                              content: (
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html: t(
                                      'confirm_box_group_allow_members_to_post_image_and_video.content'
                                    ),
                                  }}
                                />
                              ),
                              confirmBoxProps: {
                                cancelContent: <></>,
                              },
                            })

                            return
                          }

                          __isActiveImagePanelSet((prevState) => {
                            return !prevState
                          })
                        }}
                      />
                    </li>

                    {/* Create poll menu. */}
                    <li className="PostBox__footer__ul__li">
                      <a
                        className={cx('PostBox__footer__ul__a', {
                          'PostBox__footer__ul__a--list': true,
                          'PostBox__footer__ul__a--list--active':
                            __choicesActive,
                          'PostBox__footer__ul__a--disabled':
                            _shouldDisableMenuBase ||
                            __isActiveImagePanel ||
                            __isCreateCallPostActive ||
                            __isCreateVideoCallPostActive ||
                            replyPostMode == 'reply' ||
                            replyPostMode == 'edit',
                        })}
                        onClick={onToggleList}
                      />
                    </li>

                    {/* Change font size menu. */}
                    <li className="PostBox__footer__ul__li PostBox__footer__ul__li--with-menu">
                      <div
                        className={cx('Menu', {
                          'Menu--active': __isActiveMenuFontSize,
                        })}
                      >
                        <a
                          className={cx('PostBox__footer__ul__a', {
                            'PostBox__footer__ul__a--fz': true,
                            'PostBox__footer__ul__a--fz--active':
                              __postBoxFontSize > 0,
                            'PostBox__footer__ul__a--disabled':
                              !isEmpty(shareToTimeline),
                          })}
                          onClick={onToggleMenuFontSize}
                        />

                        <div className="Menu__panel Menu__panel--right Menu__panel--arrow-left">
                          <ul className="Menu__panel__ul">
                            {Array.from(Array(5)).map((_, i) => (
                              <li key={i}>
                                <a
                                  onClick={() => {
                                    __postBoxFontSizeSet(4 - i)
                                    onToggleMenuFontSize()
                                  }}
                                  className={cx('Menu__panel__ul__a', {
                                    'PostBox__footer__font-size-menu': true,
                                    [`PostBox__footer__font-size-menu--fz-${
                                      4 - i
                                    }`]: true,
                                  })}
                                >
                                  {i === 4
                                    ? `Normal size`
                                    : t('postbox.text_size', {
                                        index: 4 - i,
                                      })}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </li>

                    {/* Create call menu. */}
                    {!hideFooterCallMenu && (
                      <li className="PostBox__footer__ul__li">
                        <a
                          className={cx('PostBox__footer__ul__a', {
                            'PostBox__footer__ul__a--call': true,
                            'PostBox__footer__ul__a--call--active':
                              __isCreateCallPostActive,
                            'PostBox__footer__ul__a--disabled':
                              _shouldDisableMenuBase ||
                              __isActiveImagePanel ||
                              __choicesActive ||
                              __isCreateVideoCallPostActive ||
                              replyPostMode == 'reply' ||
                              replyPostMode == 'edit',
                          })}
                          onClick={() => {
                            __isCreateCallPostActiveSet((prevState) => {
                              return !prevState
                            })
                          }}
                        />
                      </li>
                    )}

                    {/* Create video call menu. */}
                    {!hideFooterVideoCallMenu && (
                      <li className="PostBox__footer__ul__li">
                        <a
                          className={cx('PostBox__footer__ul__a', {
                            'PostBox__footer__ul__a--video-call': true,
                            'PostBox__footer__ul__a--video-call--active':
                              __isCreateVideoCallPostActive,
                            'PostBox__footer__ul__a--disabled':
                              _shouldDisableMenuBase ||
                              __isActiveImagePanel ||
                              __choicesActive ||
                              __isCreateCallPostActive ||
                              replyPostMode == 'reply' ||
                              replyPostMode == 'edit',
                          })}
                          onClick={() => {
                            __isCreateVideoCallPostActiveSet((prevState) => {
                              return !prevState
                            })
                          }}
                        />
                      </li>
                    )}
                  </ul>
                )
              })()}

              {(() => {
                const _isDisabled = (() => {
                  if (
                    __isLoading ||
                    getUrlMetaDataRes?.isLoading ||
                    editPostRes?.isLoading
                  ) {
                    return true
                  }

                  if (
                    __isCreateCallPostActive ||
                    __isCreateVideoCallPostActive
                  ) {
                    return false
                  }

                  if (
                    (__text.length ?? 0) >= MAX_POST_TEXT_LIMIT ||
                    ((__screenshots.length ?? 0) === 0 &&
                      (__text?.trim?.()?.length ?? 0) === 0)
                  ) {
                    return true
                  }

                  if (__choicesActive) {
                    const __emptyChoiceIndex = __choices.findIndex(
                      (v) => v.length === 0
                    )

                    return __emptyChoiceIndex >= 0
                  }

                  if (replyPostMode == 'edit') {
                    if (
                      replyPost?.text == __text &&
                      replyPost?.color == __postBoxColorIndex &&
                      replyPost?.font_size == __postBoxFontSize
                    ) {
                      return true
                    }
                  }

                  return false
                })()

                return (
                  <button
                    className="Button Button--secondary Button--icon-plus"
                    ref={___$submitButton}
                    disabled={_isDisabled}
                  >
                    {t('common.post')}
                  </button>
                )
              })()}
            </div>
          </PostBoxDropzone>

          {!isNil(overlayAction) && (
            <a
              className="PostBox__overlay-action"
              onClick={() => {
                overlayAction()
              }}
            />
          )}
        </div>
      </form>

      <MentionableList
        mention={mention}
        enableMentionAll={
          !isNil(groupId) &&
          isNil(threadId) &&
          ['owner', 'moderator'].includes(groupUserRole)
        }
      />
    </>
  )
}

export default PostBox

export const PostBoxSkeletonLoading = () => {
  return (
    <div className="PostBoxSkeletonLoading">
      <Skeleton height={200} />
    </div>
  )
}
